import React, { useEffect, useState } from "react";
import { Link, Link as RouterLink } from "react-router-dom";
import logo from "../../assets/BLH_Icon.svg";
import LoginIcon from "@mui/icons-material/Login";
import { Button } from "@mui/material";

const Nav = () => {
  const [activeSection, setActiveSection] = useState("");
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      const sections = ["features", "how-it-works", "for-you", "demo"];
      let current = "";
      sections.forEach((section) => {
        if (
          window.scrollY >=
          (document.getElementById(section)?.offsetTop || 0) - 100
        ) {
          current = section;
        }
      });
      setActiveSection(current);
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const scrollToSection = (id: string) => {
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
  };

  const navButtonClass = (id: string) =>
    `text-sm font-medium transition-colors ${
      activeSection === id
        ? "text-navy"
        : "text-slate-600 hover:text-navy"
    }`;

  const MobileButton = ({
    id,
    children,
  }: {
    id: string;
    children: React.ReactNode;
  }) => (
    <button
      type="button"
      onClick={() => {
        setMobileMenuOpen(false);
        scrollToSection(id);
      }}
      className={`w-full text-left px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
        activeSection === id
          ? "text-navy bg-slate-50"
          : "text-slate-600 hover:text-navy hover:bg-slate-50"
      }`}
    >
      {children}
    </button>
  );

  return (
    <nav className="fixed top-0 left-0 right-0 z-[100] bg-white backdrop-blur-xl border-b border-slate-200 shadow-sm px-4 md:px-6 lg:px-8 py-3 md:py-4 flex items-center justify-between h-16 md:h-20">
      <Link
        to="/"
        className="nav-logo flex items-center gap-2.5 text-decoration-none no-underline"
        onClick={() => setMobileMenuOpen(false)}
      >
        <div className="w-[56px] h-[56px] sm:w-[72px] sm:h-[72px] md:w-[85px] md:h-[85px] -mt-2 md:-mt-8 rounded-lg flex items-center justify-center p-1.5 shrink-0">
          <img
            src={logo}
            alt="Business Lending Hub"
            className="w-full h-full object-cover rounded"
          />
        </div>

        <span className="hidden sm:inline text-[15px] sm:text-xl font-bold text-navy tracking-tight whitespace-nowrap">
          The Business Lending Hub
        </span>
      </Link>

      {/* Mobile: Login always visible + hamburger for section links */}
      <div className="flex items-center gap-2 md:hidden">
        <Button
          component={RouterLink}
          to="/login"
          variant="contained"
          startIcon={<LoginIcon />}
          sx={{
            borderRadius: 2,
            py: 1,
            px: 1.5,
            fontWeight: 600,
            fontSize: "0.95rem",
            minWidth: 0,
          }}
        >
          Login
        </Button>

        <button
          type="button"
          aria-label="Open menu"
          className="inline-flex items-center justify-center rounded-lg border border-slate-200 bg-white p-2"
          onClick={() => setMobileMenuOpen((v) => !v)}
        >
          <svg
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <line x1="3" y1="12" x2="21" y2="12" />
            <line x1="3" y1="6" x2="21" y2="6" />
            <line x1="3" y1="18" x2="21" y2="18" />
          </svg>
        </button>
      </div>

      {/* Desktop/tablet links */}
      <ul className="nav-links hidden md:flex items-center gap-6 md:gap-8 lg:gap-10 list-none m-0">
        <li>
          <button
            type="button"
            onClick={() => scrollToSection("features")}
            className={navButtonClass("features")}
          >
            Features
          </button>
        </li>
        <li>
          <button
            type="button"
            onClick={() => scrollToSection("how-it-works")}
            className={navButtonClass("how-it-works")}
          >
            How It Works
          </button>
        </li>
        <li>
          <button
            type="button"
            onClick={() => scrollToSection("for-you")}
            className={navButtonClass("for-you")}
          >
            For You
          </button>
        </li>
        <li>
          <Button
            component={RouterLink}
            to="/login"
            variant="contained"
            startIcon={<LoginIcon />}
            sx={{
              borderRadius: 2,
              py: 1.5,
              fontWeight: 600,
              fontSize: "1rem",
              mt: 1,
            }}
          >
            Login
          </Button>
        </li>
      </ul>

      {/* Mobile dropdown links */}
      {mobileMenuOpen && (
        <div className="md:hidden absolute top-[64px] left-0 right-0 bg-white border-b border-slate-200 shadow-sm">
          <ul className="flex flex-col gap-1 p-3">
            <li>
              <MobileButton id="features">Features</MobileButton>
            </li>
            <li>
              <MobileButton id="how-it-works">
                How It Works
              </MobileButton>
            </li>
            <li>
              <MobileButton id="for-you">For You</MobileButton>
            </li>
            <li className="pt-2">
              <Button
                component={RouterLink}
                to="/login"
                variant="contained"
                startIcon={<LoginIcon />}
                fullWidth
                sx={{
                  borderRadius: 2,
                  py: 1.5,
                  fontWeight: 600,
                  fontSize: "1rem",
                  mt: 0,
                }}
                onClick={() => setMobileMenuOpen(false)}
              >
                Login
              </Button>
            </li>
          </ul>
        </div>
      )}
    </nav>
  );
};

export default Nav;
